import React from 'react'
import { connect } from 'react-redux'
import { wrapper } from '../../../redux/store'
import Header from '../../../components/public/shared/Header'
import Footer from '../../../components/public/shared/Footer'
import ChallengeSpaceCard from '../../../components/public/challenge-space/ChallengeSpaceCard'
import ChallengeSpaceFilters from '../../../components/public/challenge-space/ChallengeSpaceFilters'
import { setChallengeSpaces } from '../../../redux/actions/challenge-spaces'
import { setSelectedOrganisationBySlug } from '../../../redux/actions/organisations'
import { isAuthenticated } from '../../../utils/auth'
import Image from 'next/image'
import Link from 'next/link'
import headerImg from '../../../public/static/img/office.jpg'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft } from '@fortawesome/pro-light-svg-icons'

const basePath = process.env.BASE_PATH

interface OrganisationChallengeSpacesProps {
	dispatch: any
	selectedOrganisation: any
	challengeSpaces: any
	organisations: any
	subjects: any
	authenticated: boolean
	totalItems: number
}

interface OrganisationChallengeSpaces {
	isClient: boolean
	selectedSubject: any
	clientSpaces: any[]
	spaceParams: {
		include: string
		page: { offset: number; limit: number }
		filter: {
			subject_id?: string
			is_public: number
			state: number
			organisation_id: string
		}
		sort: string
	}
}

export const getServerSideProps = wrapper.getServerSideProps(
	(store) => async (ctx) => {
		await store.dispatch(setSelectedOrganisationBySlug(ctx.query.slug))
		let storeData = store.getState()

		if (storeData.public && storeData.public.selectedOrganisation) {
			const spaceParams = {
				include: 'subject',
				page: {
					offset: 0,
					limit: 18,
				},
				filter: {
					is_public: 1,
					state: 1,
					organisation_id: storeData.public.selectedOrganisation['id'],
				},
				sort: '-created_at',
			}
			await store.dispatch(setChallengeSpaces(spaceParams))
			storeData = store.getState()

			return {
				props: {
					challengeSpaces: storeData.public.challengeSpace,
					subjects: storeData.public.subject,
					selectedOrganisation: storeData.public.selectedOrganisation,
					organisations: storeData.public.organisation,
					authenticated: isAuthenticated(ctx),
					title: `${storeData.public.selectedOrganisation['attributes'].name} | Challenge Spaces`,
					description:
						storeData.public.selectedOrganisation['attributes'].description,
					url: `${basePath}/challenge-spaces/organisation/${ctx.query.slug}`,
				},
			}
		} else {
			return {
				notFound: true,
			}
		}
	}
)

const mapStateToProps = (state) => {
	return {
		challengeSpaces: state.public.challengeSpace,
		selectedOrganisation: state.public.selectedOrganisation,
		organisations: state.public.organisation,
		totalItems: state.public.meta['/challenge-spaces']
			? state.public.meta['/challenge-spaces']['meta']['totalItems']
			: 0,
	}
}
class ChallengeSpaces extends React.Component<
	OrganisationChallengeSpacesProps,
	OrganisationChallengeSpaces
> {
	constructor(props) {
		super(props)

		this.state = {
			isClient: false,
			selectedSubject: null,
			clientSpaces: [],
			spaceParams: {
				include: 'subject',
				page: {
					offset: 0,
					limit: 18,
				},
				filter: {
					is_public: 1,
					state: 1,
					organisation_id: this.props.selectedOrganisation.id,
				},
				sort: '-created_at',
			},
		}

		this.loadMore = this.loadMore.bind(this)
		this.onFilterChange = this.onFilterChange.bind(this)
	}

	componentDidMount() {
		// Redirect if user wants to submit a challenge and is logged in
		this.setState({ isClient: true })
	}

	loadMore() {
		this.setState(
			{
				spaceParams: {
					...this.state.spaceParams,
					page: {
						...this.state.spaceParams.page,
						offset: this.state.spaceParams.page.offset + 9,
					},
					filter: {
						...this.state.spaceParams.filter,
						organisation_id: this.props.selectedOrganisation.id,
					},
				},
			},
			() =>
				this.props.dispatch(setChallengeSpaces(this.state.spaceParams, true))
		)
	}

	onFilterChange(filters: any) {
		this.setState(
			{
				spaceParams: {
					...filters,
				},
			},
			() =>
				this.props.dispatch(setChallengeSpaces(this.state.spaceParams, false))
		)
	}

	render() {
		const {
			authenticated,
			challengeSpaces,
			organisations,
			totalItems,
			selectedOrganisation,
		} = this.props
		const subjects = Object.keys(this.props.subjects).map((key) => {
			return this.props.subjects[key]
		})

		return (
			<div className="wrapper overflow-x-hidden">
				<Header authenticated={authenticated} />
				<main className="pt-20 relative z-20">
					<div className="relative z-20 bg-white border-b">
						<div className="container mx-auto px-4 pt-8 pb-4 relative z-20">
							<div className="w-full md:w-1/2">
								<Link href="/challenge-spaces" passHref>
									<h2 className="cursor-pointer hover:text-purple-800 transition-colors duration-300 text-xl mb-2 text-purple-900 font-sans font-bold">
										<FontAwesomeIcon className="mr-2" icon={faArrowLeft} />{' '}
										Challenge Spaces
									</h2>
								</Link>
								<h1 className="text-3xl mb-3 text-gray-900 font-sans font-bold">
									{selectedOrganisation.attributes.name}
								</h1>
								<p className="mb-4">
									{selectedOrganisation.attributes.description}
								</p>
							</div>
						</div>
						<div className="bg-gradient-to-r z-10 absolute from-white h-full right-0 bottom-0 top-0 w-7/12"></div>
						<div className="absolute right-0 bottom-0 top-0 h-full w-7/12 opacity-50">
							<Image
								layout="fill"
								objectFit="cover"
								objectPosition="center"
								src={headerImg}
								alt="Challenge spaces"
							/>
						</div>
					</div>

					<div className="relative mb-32 z-10 pt-4">
						<ChallengeSpaceFilters
							currentValues={this.state.spaceParams}
							hideOrganizers={true}
							onChange={this.onFilterChange}
						/>
						<div className="container mx-auto flex flex-wrap">
							{challengeSpaces &&
								Object.keys(challengeSpaces).map((key, index) => {
									const subject = subjects.find(
										(s) =>
											s.id ===
											challengeSpaces[key].relationships?.subject?.data?.id
									)
									const organisation =
										organisations[
											challengeSpaces[key].relationships.organisation.data.id
										]

									return (
										<div
											className="px-4 w-full md:w-1/2 lg:w-1/3 mb-8"
											key={index}
										>
											<ChallengeSpaceCard
												space={challengeSpaces[key]}
												subject={subject}
												organisation={organisation}
												key={index}
											/>
										</div>
									)
								})}
						</div>
						<div
							className="container mx-auto flex items-start justify-center"
							style={{ minHeight: !challengeSpaces ? '600px' : 'auto' }}
						>
							{challengeSpaces &&
								totalItems > Object.keys(challengeSpaces).length && (
									<button
										onClick={this.loadMore}
										type="button"
										className="bg-yellow-300 transition-colors duration-300 text-gray-900 hover:bg-yellow-200 mx-2 z-10 relative my-2 flex-grow-0"
									>
										Load more
									</button>
								)}
							{!challengeSpaces && (
								<p className="font-sansSemiBold font-semibold">
									No results found
								</p>
							)}
						</div>
					</div>
				</main>
				<Footer />
			</div>
		)
	}
}
export default connect(mapStateToProps)(ChallengeSpaces)
